import { Component, OnInit, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RealEstateService } from '../../services/real-estate.service';
import * as _ from 'lodash';
import { DataService } from '../../services/data.service';
import { ConfigService } from '../../services/config.service';
import {
  BUSINESS_UNITS,
  LAND_SERVICES_DEPARTMENTS,
  LOCATION_TYPES,
  PROJECT_TYPES,
  STATES,
} from '../common/real-estate-form-options';
import { GraphUser } from '../../models/graph-user.model';
import { MatDatepicker } from '@angular/material/datepicker';
import { RREFormStatus } from '../../real-estate-form-dashboard/common/real-estate-form-dashboard.types';

@Component({
  selector: 'app-land-services-project-detail-info',
  templateUrl: './land-services-project-detail-info.component.html',
  styleUrls: ['./land-services-project-detail-info.component.scss'],
  standalone: false,
})
export class LandServicesProjectDetailInfoComponent implements OnInit {
  @Input() reviewMode = false;
  @Input() landServicesDetailForm: FormGroup;
  @Input() disposal = false;
  @Input() acquisition = false;
  @Input() formStatus: RREFormStatus;

  RREFormStatus = RREFormStatus;

  states = STATES;
  departments = LAND_SERVICES_DEPARTMENTS;
  businessUnits = BUSINESS_UNITS;
  projectTypes = PROJECT_TYPES;
  locationTypes = LOCATION_TYPES;
  projectManagers = [''];
  sponsors = [''];
  assetContacts = [''];

  projectManager = {
    name: '',
    email: '',
  };
  sponsor = {
    name: '',
    email: '',
  };
  assetContact = {
    name: '',
    email: '',
  };

  projectManagerUserDetails: GraphUser[] = [];
  filteredProjectManagerUserDetails: GraphUser[] = [];

  sponsorUserDetails: GraphUser[] = [];
  filteredSponsorUserDetails: GraphUser[] = [];

  assetUserDetails: GraphUser[] = [];
  filteredAssetUserDetails: GraphUser[] = [];

  startDatePicker!: MatDatepicker<any>;
  serviceDatePicker!: MatDatepicker<any>;
  completionDatePicker!: MatDatepicker<any>;

  minStartDate = new Date();
  todaysDate = new Date();

  constructor(
    private dialog: MatDialog,
    private realEstateService: RealEstateService,
    private dataService: DataService,
    private config: ConfigService
  ) {}

  ngOnInit() {
    this.setupContactSearchSubscription();
  }

  onStateChange(): void {
    this.createProjectId();
  }

  onDepartmentChange(): void {
    this.createProjectId();
  }

  onBusinessUnitChange(bu: string): void {
    const value = this.businessUnits.find((item) => item.value === bu).fundingProject;
    this.landServicesDetailForm.get('fundingProject').setValue(value);
  }

  onProjectTypeChange(value: string): void {
    this.landServicesDetailForm.get('projectClass').setValue(value);
  }

  createProjectId(): void {
    const state = this.landServicesDetailForm.get('state').value;
    const department = this.landServicesDetailForm.get('department').value;

    if (state && department) {
      this.getProjectDetails(state, department);
    } else {
      console.warn('State and Department must be filled in before making the call.');
    }
  }

  getProjectDetails(state: string, department: string): void {
    const body = {
      state: state,
      department: department,
      adaptive_id: null,
    };
    this.realEstateService.getProjectSetupData(body).subscribe((response) => {
      // Prefill the form with backend data
      const projectIdControl = this.landServicesDetailForm.get('projectId');
      if (projectIdControl) {
        projectIdControl.setValue(response.project_id);
      }

      (error) => {
        console.error('Error:', error);
      };
    });
  }

  descriptionLength(): number {
    const description = this.landServicesDetailForm.get('projectDescription').value || '';
    return description.length;
  }

  setupContactSearchSubscription(): void {
    this.landServicesDetailForm
      .get('projectManager')
      .valueChanges.subscribe((searchInput: string) => {
        if (searchInput && searchInput.length > 2) {
          // Call the getContactOptions() method when the user types at least 3 characters
          this.getContactOptions(searchInput, 'projectManager');
        } else {
          // Clear the filtered user details if the input is empty or too short
          this.filteredProjectManagerUserDetails = [];
        }
      });

    this.landServicesDetailForm.get('sponsor').valueChanges.subscribe((searchInput: string) => {
      if (searchInput && searchInput.length > 2) {
        // Call the getContactOptions() method when the user types at least 3 characters
        this.getContactOptions(searchInput, 'sponsor');
      } else {
        // Clear the filtered user details if the input is empty or too short
        this.filteredSponsorUserDetails = [];
      }
    });

    this.landServicesDetailForm.get('assetContact').valueChanges.subscribe(() => {
      this.getAssetContactOptions();
    });
  }

  getContactOptions(
    searchInput: string,
    controlType: 'projectManager' | 'sponsor' | 'contractAdmin' | 'additionalContact'
  ): void {
    this.realEstateService
      .userSearch(searchInput)
      .then((users) => {
        const orderedUsers = _.orderBy(users, ['surname', 'givenName']);

        switch (controlType) {
          case 'projectManager':
            this.projectManagerUserDetails = orderedUsers;
            this.filteredProjectManagerUserDetails = [...this.projectManagerUserDetails];
            this.displayGraphUser(this.projectManagerUserDetails, 'projectManager');
            break;
          case 'sponsor':
            this.sponsorUserDetails = orderedUsers;
            this.filteredSponsorUserDetails = [...this.sponsorUserDetails];
            this.displayGraphUser(this.sponsorUserDetails, 'sponsor');
            break;
        }
      })
      .catch((error) => {
        console.error('Error fetching users:', error);
      });
  }

  getAssetContactOptions(): void {
    const adGroup = this.config.getConfig('LandServicesAssetContactGroup');
    this.dataService.getGroupId(adGroup, { useDisplayName: true }).subscribe((result) => {
      if (!result || !(result as any).value) return;

      const groupId = (result as any).value[0].id;
      this.realEstateService.assetContactsUserSearch(groupId).subscribe(
        (result) => {
          this.assetUserDetails = _.orderBy((result as any).value, ['surname', 'givenName']);
          this.filteredAssetUserDetails = [...this.assetUserDetails];
          this.displayGraphUser(this.assetUserDetails, 'asset');
        },
        (error) => {
          console.error('Error fetching users:', error);
        }
      );
    });
  }

  displayGraphUser(
    users: GraphUser[],
    target: 'projectManager' | 'sponsor' | 'asset'
  ): any | undefined {
    const formatted = users.map((user) => ({
      ...user,
      displayName: user.displayName,
      mail: user.mail,
      formattedValue: !user
        ? ''
        : user.displayName && user.mail
          ? `${user.displayName} (${user.mail})`
          : '',
    }));

    switch (target) {
      case 'projectManager':
        this.filteredProjectManagerUserDetails = formatted;
        if (this.projectManager.name) {
          const value = formatted.find((a) => a.mail === this.projectManager.email);
          if (value) {
            this.landServicesDetailForm.get('projectManager').setValue(value);
            this.projectManager = null;
          }
        }
        break;
      case 'sponsor':
        this.filteredSponsorUserDetails = formatted;
        if (this.sponsor.name) {
          const value = formatted.find((a) => a.mail === this.sponsor.email);
          if (value) {
            this.landServicesDetailForm.get('sponsor').setValue(value);
            this.sponsor = null;
          }
        }
        break;

      case 'asset':
        this.filteredAssetUserDetails = formatted;
        if (this.assetContact.name) {
          const value = formatted.find((a) => a.mail === this.assetContact.email);
          if (value) {
            this.landServicesDetailForm.get('assetContact').setValue(value);
            this.assetContact = null;
          }
        }
        break;
    }
  }

  displayUser(value: GraphUser) {
    return value ? value.formattedValue : '';
  }

  onServiceDateChange(inServiceDate: Date) {
    const completionDate = new Date(inServiceDate);
    completionDate.setMonth(completionDate.getMonth() + 3);
    this.landServicesDetailForm.get('completionDate').setValue(completionDate);
  }
}
